import Tag from 'components/Tag'
import ExternalLink from 'components/UI/ExternalLink'
import Svg from 'components/UI/SVG'
import UnstyledMarkdown from 'components/UI/UnstyledMarkdown'
import { ArticlesArrowComponent } from 'components/knowledge-base/Categories'
import CircumventCensorshipFeaturesIcon from 'images/circumvent-censorship.svg'
import ConfigFeaturesIcon from 'images/config-features-icon.svg'
import FlexibleConnectivityFeaturesIcon from 'images/flexible-connectivity-features-icon.svg'
import LanTrafficFeaturesIcon from 'images/lan-traffic-features-icon.svg'
import LargeNetworkFeaturesIcon from 'images/large-network-features-icon.svg'
import NetworkOptionsFeaturesIcon from 'images/network-options-icon.svg'
import NoLogsFeaturesIcon from 'images/no-logs-features-icon.svg'
import PacketSizeFeaturesIcon from 'images/packet-size-features-icon.svg'
import PortForwardingFeaturesIcon from 'images/port-forwarding-features-icon.svg'
import RobertFeaturesIcon from 'images/robert-features-icon.svg'
import ScribeForceFeaturesIcon from 'images/scribe-force-features-icon.svg'
import SimpleClientsFeaturesIcon from 'images/simple-clients-features-icon.svg'
import SplitTunnelingFeaturesIcon from 'images/split-tunneling-features-icon.svg'
import StaticIpFeaturesIcon from 'images/static-ip-features-icon.svg'
import EncryptionFeaturesIcon from 'images/strongest-encryption-features-icon.svg'
import FreeFeaturesIcon from 'images/use-for-free-features-icon.svg'
import React, { ReactElement, ReactNode } from 'react'
import { Box, Flex, Text, get } from 'theme-ui'
import getEnglishStrings from 'utils/getEnglishStrings'
import slugify from 'utils/slugify'

export interface FeaturesPageFeatureContent {
  title?: string | null
  description?: string | null
  icon: React.ElementType
  href?: string
}

export default function Features({
  featuresData,
}: {
  featuresData: Queries.KnowledgeBaseFeaturesPageQuery['directus']['features_page']
}): ReactElement {
  const featuresDataStrings = getEnglishStrings(featuresData?.strings)
  const featureList: FeaturesPageFeatureContent[] = [
    {
      title: featuresDataStrings?.large_network_title,
      description: featuresDataStrings?.large_network_subtitle,
      icon: LargeNetworkFeaturesIcon,
      href: '/vpn-servers',
    },
    {
      title: featuresDataStrings?.robert_title,
      description: featuresDataStrings?.robert_subtitle,
      icon: RobertFeaturesIcon,
    },
    {
      title: featuresDataStrings?.identifying_logs_title,
      description: featuresDataStrings?.identifying_logs_subtitle,
      icon: NoLogsFeaturesIcon,
    },
    {
      title: featuresDataStrings?.strongest_encryption_title,
      description: featuresDataStrings?.strongest_encryption_subtitle,
      icon: EncryptionFeaturesIcon,
    },
    {
      title: featuresDataStrings?.config_generators_title,
      description: featuresDataStrings?.config_generators_subtitle,
      icon: ConfigFeaturesIcon,
    },
    {
      title: featuresDataStrings?.scribeforce_title,
      description: featuresDataStrings?.scribeforce_subtitle,
      icon: ScribeForceFeaturesIcon,
    },
    {
      title: featuresDataStrings?.simple_clients_title,
      description: featuresDataStrings?.simple_clients_subtitle,
      icon: SimpleClientsFeaturesIcon,
    },
    {
      title: featuresDataStrings?.use_for_free_title,
      description: featuresDataStrings?.use_for_free_subtitle,
      icon: FreeFeaturesIcon,
    },
    {
      title: featuresDataStrings?.static_ips_title,
      description: featuresDataStrings?.static_ips_subtitle,
      icon: StaticIpFeaturesIcon,
    },
    {
      title: featuresDataStrings?.port_forwarding_title,
      description: featuresDataStrings?.port_forwarding_subtitle,
      icon: PortForwardingFeaturesIcon,
    },
    {
      title: featuresDataStrings?.split_tunneling_title,
      description: featuresDataStrings?.split_tunneling_subtitle,
      icon: SplitTunnelingFeaturesIcon,
    },
    {
      title: featuresDataStrings?.packet_size,
      description: featuresDataStrings?.packet_size_subtitle,
      icon: PacketSizeFeaturesIcon,
    },
    {
      title: featuresDataStrings?.lan_traffic,
      description: featuresDataStrings?.lan_traffic_subtitle,
      icon: LanTrafficFeaturesIcon,
    },
    {
      title: featuresDataStrings?.flexible_connectivity,
      description: featuresDataStrings?.flexible_connectivity_subtitle,
      icon: FlexibleConnectivityFeaturesIcon,
    },
    {
      title: featuresDataStrings?.circumvent_censorship,
      description: featuresDataStrings?.circumvent_censorship_subtitle,
      icon: CircumventCensorshipFeaturesIcon,
    },
    {
      title: featuresDataStrings?.network_options_title,
      description: featuresDataStrings?.network_options_subtitle,
      icon: NetworkOptionsFeaturesIcon,
    },
  ]
  return (
    <Flex
      sx={{
        flexDirection: 'column',
        position: 'relative',
        width: ['100vw', '100vw', '100%'],
        alignItems: 'center',
      }}
    >
      <Box sx={{ mb: '4.8rem' }}>
        <Tag title={featuresDataStrings?.feature_list_title ?? ''} />
      </Box>
      <FeatureWrapper>
        {featureList.map(featureListItem => {
          return (
            <Feature
              key={featureListItem.title}
              title={featureListItem.title ?? ''}
              description={featureListItem.description ?? ''}
              icon={featureListItem.icon}
              href={featureListItem.href}
            />
          )
        })}
      </FeatureWrapper>
    </Flex>
  )
}

export function Feature({
  title,
  description,
  icon,
  href,
}: {
  title: string | undefined
  description: string | undefined
  icon: React.ElementType
  href?: string
}): ReactElement {
  return (
    <Flex data-testid={title?.toLowerCase().replaceAll(' ', '-').replaceAll('.', '')}>
      {/* Adding those as external links to prevent prefetching */}
      <ExternalLink href={href ?? slugify(title?.replaceAll('.', ''))}>
        <Flex
          sx={{
            maxWidth: '29rem',
            flexDirection: 'column',
            alignItems: 'center',
            '.features-arrow, .features-description, .features-image': {
              transition: theme => `${get(theme, 'transitions.default')}`,
            },
            '.features-image svg': {
              willChange: 'transform',
              transformOrigin: 'center',
              transform: 'scale(1)',
              backfaceVisibility: 'hidden',
              perspective: '1000px',
            },
            ':hover': {
              '.features-arrow ': {
                transform: 'translateX(1rem)',
              },
              '.features-description': {
                color: 'white',
              },
              '.features-image': {
                transform: 'scale(1.1)',
              },
            },
          }}
        >
          <Flex
            className="features-image"
            sx={{
              maxWidth: '14.8rem',
              maxHeight: '14.5rem',
              marginBottom: '1.6rem',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Svg svg={icon} sx={{ objectFit: 'scale-down' }} />
          </Flex>
          <Flex sx={{ gap: '0.8rem', alignItems: 'center', paddingBottom: '1.6rem' }}>
            <Text variant="heading2" sx={{ color: 'white', fontWeight: 'bold' }} as="h3">
              {title}
            </Text>
            <ArticlesArrowComponent
              className="features-arrow"
              sxx={{
                width: '2.4rem',
                height: '2.4rem',
                path: {
                  strokeOpacity: 1,
                },
              }}
            />
          </Flex>
          <Text variant="body2" className="features-description">
            <UnstyledMarkdown sxx={{ color: 'white80', textAlign: 'center' }}>
              {description ?? ''}
            </UnstyledMarkdown>
          </Text>
        </Flex>
      </ExternalLink>
    </Flex>
  )
}

export function FeatureWrapper({ children }: { children: ReactNode }): ReactElement {
  return (
    <Flex
      sx={{
        width: '100%',
        maxWidth: '98rem',
        flexDirection: ['column', 'row', 'row'],
        flexWrap: ['nowrap', 'wrap', 'wrap'],
        gap: '4.8rem',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {children}
    </Flex>
  )
}
